import { CheckboxField, Form, TextField, useForm, yup, yupResolver } from 'form';
import { type I18nScopedKey, type I18nTranslateFunction, useTranslation } from 'i18n';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Link, Typography, useBreakpoint } from 'ui';

import { TermsAndPolicyAgreement } from '../TermsAndPolicyAgreement/TermsAndPolicyAgreement';

type TranslationFunction = I18nTranslateFunction<I18nScopedKey<'features'>>;
type SignInEmailFormValues = {
  email: string;
  terms?: boolean;
};

const buildSchema = (t: TranslationFunction) =>
  yup.object().shape({
    email: yup
      .string()
      .email(t('auth.signInEmailForm.validation.invalidEmail'))
      .required(t('auth.signInEmailForm.validation.emailRequired')),
  });

const buildSchemaWithTermsAndPolicy = (t: TranslationFunction) =>
  yup.object().shape({
    email: yup
      .string()
      .email(t('auth.signInEmailForm.validation.invalidEmail'))
      .required(t('auth.signInEmailForm.validation.emailRequired')),
    terms: yup
      .bool()
      .oneOf([true], t('auth.signInEmailForm.validation.termsRequired'))
      .required(t('auth.signInEmailForm.validation.termsRequired')),
  });

export const SignInEmailForm = ({
  phoneSignInRoute,
  onSubmit,
  withTermsAndPolicy = false,
  isLoading,
}: {
  phoneSignInRoute?: string;
  onSubmit: (data: Pick<SignInEmailFormValues, 'email'>) => void;
  isLoading?: boolean;
  withTermsAndPolicy?: boolean;
}) => {
  const { t } = useTranslation('features');
  const { isMobile } = useBreakpoint();
  const currentSchema = withTermsAndPolicy ? buildSchemaWithTermsAndPolicy(t) : buildSchema(t);
  const form = useForm<SignInEmailFormValues>({
    resolver: yupResolver(currentSchema),
  });

  const handleSubmit = (data: SignInEmailFormValues) => {
    onSubmit({
      email: data.email,
    });
  };

  return (
    <Form {...form} onSubmit={handleSubmit} data-testid="sign-in-email-form">
      <TextField
        type="text"
        name="email"
        size="medium"
        label={t('auth.signInEmailForm.labels.email')}
        required
        fullWidth
      />
      {withTermsAndPolicy ? (
        <CheckboxField
          name="terms"
          size={isMobile ? 'medium' : 'large'}
          data-testid="checkbox"
          required
          label={<TermsAndPolicyAgreement />}
        />
      ) : null}
      {phoneSignInRoute ? (
        <Typography variant="body2" align="right" gutterBottom>
          <Link component={RouterLink} to={phoneSignInRoute}>
            {t('auth.signInEmailForm.links.signInWithPhone')}
          </Link>
        </Typography>
      ) : null}
      <Button fullWidth type="submit" size="large" loading={isLoading}>
        {t('auth.signInEmailForm.actions.next')}
      </Button>
    </Form>
  );
};
